import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChatIcon from "@mui/icons-material/Chat";
import dayjs from "dayjs";
import { useTheme, alpha } from "@mui/material/styles";
import { adminSurfaceSx, MONO } from "./adminStyles";
import ChatTimelineDialog from "./ChatTimelineDialog";
import { adminRequest } from "../../utils/adminApi";

// Admin workout-forensics dialog (hybra #529, worker #444). Given a workout
// flag's workout_id + user email, fetches the full forensic story of that one
// workout via the functions/api/admin/getWorkoutForensics proxy → worker
// GET /api/admin/workout-forensics and renders it: provenance header (workout,
// originating conversation + draft/prescription), per-activity coach-prescribed
// vs manual-add, per-set target-vs-actual, and the session's rest events. A
// "View chat" action reuses the existing ChatTimelineDialog for the workout's
// originating conversation. The worker enforces admin auth server-side; a 404
// renders a graceful not-found state, any other failure an inline error —
// never a crash (worker #444 may not be deployed yet).

// The 10 target/actual dimension pairs a Segment can carry (activity.schema.ts,
// worker). The wire shape is SPARSE — null dims are omitted — so a pair renders
// only when at least one side is present. `unit` derives the display unit from
// the segment's companion unit fields.
const SEGMENT_DIMS = [
  { key: "count", label: "reps" },
  {
    key: "load",
    label: "load",
    // load_unit: 0 lbs / 1 kgs (WEIGHT_UNIT). Absent → unitless number.
    unit: (s) => (s.load_unit === 1 ? "kg" : s.load_unit === 0 ? "lb" : ""),
  },
  { key: "distance", label: "distance", unit: (s) => s.distance_unit ?? "" },
  {
    key: "duration_ms",
    label: "time",
    fmt: (v) => `${Math.round(v / 1000)}s`,
  },
  { key: "calories", label: "cal" },
  { key: "power_w", label: "power", unit: () => "W" },
  { key: "cadence", label: "cadence", unit: (s) => s.cadence_unit ?? "" },
  { key: "elevation_gain_m", label: "elev", unit: () => "m" },
  { key: "height_m", label: "height", unit: () => "m" },
  { key: "heart_rate_bpm", label: "HR", unit: () => "bpm" },
];

// One "target → actual" fragment per dimension the segment actually used.
// Either side may be absent (sparse): render "—" for the missing half so a
// planned-but-unperformed or freestyle-logged set still reads correctly.
export function segmentDimText(segment) {
  const parts = [];
  for (const dim of SEGMENT_DIMS) {
    const target = segment[`target_${dim.key}`];
    const actual = segment[`actual_${dim.key}`];
    if (target == null && actual == null) continue;
    const unit = dim.unit ? dim.unit(segment) : "";
    const fmt = (v) =>
      v == null ? "—" : `${dim.fmt ? dim.fmt(v) : v}${unit ? ` ${unit}` : ""}`;
    parts.push(`${dim.label} ${fmt(target)} → ${fmt(actual)}`);
  }
  return parts.join(" · ");
}

export default function WorkoutForensicsDialog({
  open,
  onClose,
  workoutId,
  userEmail,
}) {
  const theme = useTheme();
  // env-aware: prod uses the authSlice token as today; staging uses the held
  // staging token and adds the routing-only env param (adminRequest).
  const { env, token } = useSelector(adminRequest);
  const [loading, setLoading] = useState(false);
  // error: null | "notfound" | "failed" — 404 is a distinct, expected state
  // (unknown workout, or worker #444 not deployed yet), not a failure.
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  // The nested chat-timeline dialog (reused, not rebuilt) for conversation_id.
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    // Guard: without both identifiers the worker query is malformed — surface
    // the not-found state rather than firing a broken request.
    if (!userEmail || !workoutId) {
      setLoading(false);
      setData(null);
      setError("notfound");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setChatOpen(false);

    const params = new URLSearchParams({ user: userEmail, id: workoutId });
    // env is routing-only; the proxy strips it before forwarding to the worker.
    if (env === "staging") params.set("env", "staging");

    axios
      .get(`/api/admin/getWorkoutForensics?${params.toString()}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((result) => {
        if (cancelled) return;
        setData(result.data ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setError("notfound");
        } else {
          console.error("Failed to load workout forensics:", err);
          setError("failed");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, userEmail, workoutId, token, env]);

  const workout = data?.workout ?? null;
  const draft = data?.draft ?? null;
  const activities = data?.activities ?? [];
  const restEvents = data?.rest_events ?? [];

  const caption = (text) => (
    <Typography
      variant="caption"
      sx={{
        color: theme.palette.text.secondary,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        fontWeight: 700,
      }}
    >
      {text}
    </Typography>
  );

  const monoValue = (value) => (
    <Typography
      variant="caption"
      sx={{
        fontFamily: MONO,
        color: theme.palette.text.primary,
        wordBreak: "break-all",
      }}
    >
      {value || "—"}
    </Typography>
  );

  const fmtTime = (t) => (t ? dayjs(String(t).replace(" ", "T")).format("MMM D, YYYY HH:mm") : null);

  // Coach-prescribed vs manual-add badge. null = unknowable (no parsed
  // prescription activities list to match against) → NO badge; guessing
  // either way would fabricate provenance.
  const provenanceBadge = (inDraftPrescription) => {
    if (inDraftPrescription === true) {
      return (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label="prescribed"
          data-testid="forensics-prescribed-badge"
          sx={{ fontFamily: MONO }}
        />
      );
    }
    if (inDraftPrescription === false) {
      return (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label="manual add"
          data-testid="forensics-manual-badge"
          sx={{ fontFamily: MONO }}
        />
      );
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      slotProps={{ paper: { sx: adminSurfaceSx(theme) } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Workout forensics
        </Typography>
        <IconButton aria-label="Close" size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error === "notfound" ? (
          <Alert
            severity="info"
            variant="outlined"
            data-testid="forensics-notfound"
          >
            Workout not found — it may have been deleted, or the worker
            forensics route is not deployed yet.
          </Alert>
        ) : error === "failed" ? (
          <Alert
            severity="error"
            variant="outlined"
            data-testid="forensics-error"
          >
            Failed to load workout forensics.
          </Alert>
        ) : workout ? (
          <Stack spacing={2}>
            {/* ── Workout header ─────────────────────────────────────────── */}
            <Box data-testid="forensics-header">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {workout.workout_text || "(untitled workout)"}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={workout.status}
                  color={workout.status === "completed" ? "success" : "default"}
                  sx={{ fontFamily: MONO }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, fontFamily: MONO }}
              >
                {[
                  fmtTime(workout.started_at) &&
                    `started ${fmtTime(workout.started_at)}`,
                  fmtTime(workout.completed_at) &&
                    `completed ${fmtTime(workout.completed_at)}`,
                ]
                  .filter(Boolean)
                  .join(" · ") || "never started"}
              </Typography>
              {workout.gym && (
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
                >
                  <strong>Gym:</strong> {workout.gym}
                </Typography>
              )}
              {workout.notes && (
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
                >
                  <strong>Notes:</strong> {workout.notes}
                </Typography>
              )}
            </Box>

            {/* ── Provenance: conversation + draft ───────────────────────── */}
            <Box
              data-testid="forensics-provenance"
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha("#ffffff", 0.1)}`,
                backgroundColor: alpha("#ffffff", 0.03),
                p: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1,
                }}
              >
                {caption("Provenance")}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ChatIcon />}
                  data-testid="forensics-view-chat"
                  disabled={!workout.conversation_id}
                  onClick={() => setChatOpen(true)}
                >
                  View chat
                </Button>
              </Box>
              <Stack spacing={0.5}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {caption("Conversation")}
                  {monoValue(workout.conversation_id)}
                </Box>
                {workout.planned_session_id && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {caption("Planned session")}
                    {monoValue(workout.planned_session_id)}
                  </Box>
                )}
                {draft ? (
                  <Box
                    data-testid="forensics-draft"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    {caption("Draft")}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`rev ${draft.revision}`}
                      sx={{ fontFamily: MONO }}
                    />
                    {draft.status && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={draft.status}
                        sx={{ fontFamily: MONO }}
                      />
                    )}
                    {draft.prescription?.title && (
                      <Typography variant="caption" sx={{ fontFamily: MONO }}>
                        {draft.prescription.title}
                      </Typography>
                    )}
                    {draft.prescription_parse_error && (
                      // The stored prescription_json was missing/unparseable —
                      // the write side validates it, so this row is corrupt.
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label="prescription unparseable"
                        data-testid="forensics-parse-error"
                        sx={{ fontFamily: MONO }}
                      />
                    )}
                  </Box>
                ) : (
                  // draft: null is a legitimate state — the workout was never
                  // started from a coach draft.
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {caption("Draft")}
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Manual / ad-hoc (no draft)"
                      data-testid="forensics-no-draft"
                      sx={{
                        fontFamily: MONO,
                        color: theme.palette.text.secondary,
                      }}
                    />
                  </Box>
                )}
              </Stack>
            </Box>

            {/* ── Activities: per-set target vs actual ───────────────────── */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Activities ({activities.length})
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              {activities.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  No activities on this workout.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {activities.map((activity) => (
                    <Box
                      key={activity.activity_id}
                      data-testid="forensics-activity"
                      sx={{
                        borderLeft: `3px solid ${alpha(
                          theme.palette.primary.main,
                          0.5
                        )}`,
                        pl: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {activity.movement_name || activity.movement_id}
                        </Typography>
                        {provenanceBadge(activity.in_draft_prescription)}
                        {!activity.completed && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label="incomplete"
                            data-testid="forensics-incomplete-badge"
                            sx={{
                              fontFamily: MONO,
                              color: theme.palette.text.secondary,
                            }}
                          />
                        )}
                      </Box>
                      <Stack spacing={0.25}>
                        {(activity.segments ?? []).map((segment, si) => (
                          <Box
                            key={`${activity.activity_id}-seg-${segment.segment_index ?? si}`}
                            data-testid="forensics-segment"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: MONO,
                                color: theme.palette.text.secondary,
                                flexShrink: 0,
                              }}
                            >
                              #{(segment.segment_index ?? si) + 1}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: MONO }}
                            >
                              {segmentDimText(segment) || "(no dimensions)"}
                              {segment.rest_ms != null &&
                                ` · rest ${Math.round(segment.rest_ms / 1000)}s`}
                            </Typography>
                            {segment.set_type && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={segment.set_type}
                                sx={{ fontFamily: MONO }}
                              />
                            )}
                            {segment.skipped && (
                              <Chip
                                size="small"
                                color="warning"
                                variant="outlined"
                                label="skipped"
                                data-testid="forensics-skipped-badge"
                                sx={{ fontFamily: MONO }}
                              />
                            )}
                            {typeof segment.success === "boolean" && (
                              <Chip
                                size="small"
                                color={segment.success ? "success" : "error"}
                                variant="outlined"
                                label={segment.success ? "make" : "miss"}
                                sx={{ fontFamily: MONO }}
                              />
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            {/* ── Rest events ────────────────────────────────────────────── */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Rest events ({restEvents.length})
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              {restEvents.length === 0 ? (
                <Typography
                  variant="body2"
                  data-testid="forensics-rest-empty"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  No rest events in this workout's window.
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {restEvents.map((event, i) => (
                    <Box
                      key={`rest-${i}`}
                      data-testid="forensics-rest-event"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      {event.outcome && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={event.outcome}
                          sx={{ fontFamily: MONO }}
                        />
                      )}
                      <Typography variant="caption" sx={{ fontFamily: MONO }}>
                        {event.exercise ?? "—"}
                        {event.set_index != null && ` set ${event.set_index}`}
                        {" · "}
                        prescribed {event.prescribed_s ?? "—"}s → actual{" "}
                        {event.actual_s ?? "—"}s
                        {event.override_s != null &&
                          ` (override ${event.override_s}s)`}
                      </Typography>
                      {/* Events carrying a workout_id are exact-pinned to this
                          workout; legacy events are time-window matched. */}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={event.workout_id ? "pinned" : "windowed"}
                        data-testid={
                          event.workout_id
                            ? "forensics-rest-pinned"
                            : "forensics-rest-windowed"
                        }
                        sx={{
                          fontFamily: MONO,
                          color: theme.palette.text.secondary,
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        ) : (
          <Alert
            severity="info"
            variant="outlined"
            data-testid="forensics-notfound"
          >
            Workout not found — it may have been deleted, or the worker
            forensics route is not deployed yet.
          </Alert>
        )}
      </DialogContent>
      {/* Reuse the existing chat-timeline dialog for the originating
          conversation — the flag → forensics → chat drill-down path. */}
      <ChatTimelineDialog
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        conversationId={workout?.conversation_id}
        userEmail={userEmail}
      />
    </Dialog>
  );
}
